/* eslint-disable guard-for-in */
import Select, { SingleValue } from 'react-select';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  PaymentSelectOption,
  SimpleOptionChain,
  SimpleSingleValueChain,
} from '@/components';
import { useAppSelector } from '@/redux/hooks';
import { paymentSelectors } from '@/redux/selectors';
import { newPaymentMethod, PaymentDictionary } from '@/config';
import { PaymentMethod } from '@/models';
import { PaymentContext, PaymentContextProps } from '@/contexts';
import { useTranslation } from 'react-i18next';

import styles from '../_inputs/InputSelect/InputSelect.module.sass';

type TariffsPaymentMethodSelectorProps = {
  tariffCost: number;
  onPaymentMethodChange: (paymentMethod: PaymentMethod) => void;
};

const TariffsPaymentMethodSelector = ({
  tariffCost,
  onPaymentMethodChange,
}: TariffsPaymentMethodSelectorProps) => {
  const { t, i18n } = useTranslation();
  const LoadMoreOption: PaymentSelectOption = {
    label: t('translation.show_more'),
    value: 'showMore',
  };
  const { paymentPlans, paymentLoading } = useAppSelector(
    paymentSelectors.getPaymentSlice,
  );
  const [options, setOptions] = useState<PaymentSelectOption[]>([]);
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const [hasSearchStarted, setHasSearchStarted] = useState(false);
  const { selectedOption, setSelectedOption, calculateDiscount } = useContext(
    PaymentContext,
  ) as PaymentContextProps;

  // List of payment methods that front end knows about
  const paymentList: PaymentSelectOption[] = useMemo(() => {
    return Object.values(PaymentDictionary).map((item) => ({
      label: item.name,
      value: item.symbol,
      discount: calculateDiscount(item.symbol),
    }));
  }, [paymentPlans]);

  // List of payment methods that are allowed for current deposit amount
  const allowedFilteredAssets = useMemo(() => {
    if (paymentPlans === null) return [];

    const filteredAssets: string[] = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const assetId in paymentPlans.allowedAssets) {
      const assetSymbol = paymentPlans.allowedAssets[assetId];
      const assetDiscount =
        paymentPlans?.assetsDiscounts?.[assetSymbol] !== undefined
          ? (100 - paymentPlans.assetsDiscounts[assetSymbol]) / 100
          : 0;
      if (
        paymentPlans.assetsMinAmounts?.[assetSymbol] <=
        (assetDiscount ? tariffCost * assetDiscount : tariffCost)
      ) {
        filteredAssets.push(assetSymbol);
      }
    }

    return filteredAssets;
  }, [paymentPlans, tariffCost]);

  // The List of payment methods
  //  that are allowed for current deposit amount and front end knows about and possible new payment methods
  //  with changed label if matched with assetsAliases from backend
  const paymentOptions = (): PaymentSelectOption[] => {
    return allowedFilteredAssets.map((asset) => {
      const option = paymentList.find((item) => asset === item.value);
      return option !== undefined
        ? {
            ...option,
            label: paymentPlans?.assetsAliases[asset] || option.label,
            discount: calculateDiscount(asset),
          }
        : {
            label: paymentPlans?.assetsAliases[asset] || asset,
            value: asset,
            discount: calculateDiscount(asset),
          };
    });
  };

  useEffect(() => {
    if (selectedOption) {
      const paymentMethod = PaymentDictionary[selectedOption.value];
      onPaymentMethodChange(
        paymentMethod || newPaymentMethod(selectedOption.value),
      );
    }
  }, [selectedOption]);

  useEffect(() => {
    const filteredOptions = paymentOptions();
    setOptions(() => {
      return filteredOptions.length > 4
        ? [...filteredOptions.slice(0, 4).concat(LoadMoreOption)]
        : [...filteredOptions];
    });
    const isOptionAvailable = filteredOptions.some(
      (option) => option.value === selectedOption?.value,
    );
    setSelectedOption(isOptionAvailable ? selectedOption : filteredOptions[0]);
    setHasSearchStarted(false);
  }, [tariffCost, i18n.language, paymentPlans]);

  const loadMoreOptions = () => {
    setOptions(() => {
      return [...paymentOptions()];
    });
  };

  const handleChange = (newOption: SingleValue<PaymentSelectOption>) => {
    if ((newOption as PaymentSelectOption).value === 'showMore') {
      loadMoreOptions();
    } else {
      setSelectedOption(newOption as PaymentSelectOption);
      setMenuIsOpen(false);
    }
  };

  const handleInputChange = (inputValue: string) => {
    if (hasSearchStarted) return;
    if (inputValue !== '') {
      loadMoreOptions();
      setHasSearchStarted(true);
    }
  };

  return (
    <Select
      options={[...options]}
      value={selectedOption}
      isSearchable
      onChange={handleChange}
      isDisabled={paymentLoading}
      menuIsOpen={menuIsOpen}
      className={styles.select}
      classNamePrefix="chain-select"
      onInputChange={handleInputChange}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      closeMenuOnSelect={false}
      components={{
        Option: SimpleOptionChain,
        SingleValue: SimpleSingleValueChain,
      }}
    />
  );
};

export default TariffsPaymentMethodSelector;
